// Components/RedeemCodeSection.jsx
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';

/**
 * Column-stacked redeem section with inline right-side text per code.
 *
 * Props:
 * - title: string
 * - placeholder: string
 * - addButtonLabel?: string ("Pridėti")
 * - applyButtonLabel?: string ("Pritaikyti")
 * - checkingLabel?: string ("Tikrinama…")
 * - items: Array<{ id: string, rightText?: string }>
 * - onApply: (code: string) => Promise<{ ok: boolean, errorText?: string, item?: { id: string, rightText?: string } }>
 * - onRemove: (id: string) => Promise<void> | void
 * - singleUse?: boolean  // when true, at most one code; "Pridėti" hidden only when a code exists
 */
export default function RedeemCodeSection({
  title,
  placeholder,
  addButtonLabel = 'Pridėti',
  applyButtonLabel = 'Pritaikyti',
  checkingLabel = 'Tikrinama…',
  items = [],
  onApply,
  onRemove,
  singleUse = false,
}) {
  const [open, setOpen] = React.useState(false);
  const [code, setCode] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState('');

  const hasItems = items.length > 0;

  const handleApply = async () => {
    const c = (code || '').trim();
    if (!c) return;

    // Prevent duplicates
    if (items.some((x) => x.id === c)) {
      // In both modes, treat duplicate as no-op success UX-wise
      setCode('');
      setErr('');
      setOpen(false);
      return;
    }

    // Enforce single-use: block second insertion
    if (singleUse && hasItems) {
      setErr('Galite pritaikyti tik vieną kodą');
      return;
    }

    setLoading(true);
    setErr('');
    try {
      const result = (await onApply?.(c)) || { ok: false };
      if (!result.ok) {
        setErr(result.errorText || 'Nepavyko pritaikyti kodo.');
        return;
      }
      // After success, clear input and collapse
      setCode('');
      setOpen(false);
    } catch {
      setErr('Nepavyko pritaikyti kodo.');
    } finally {
      setLoading(false);
    }
  };

  // Input visibility:
  // - Always toggled by `open`
  // - In singleUse, suppress input when a code already exists
  const showInput = open && (!singleUse || !hasItems);

  // Add button visibility:
  // - Multi-use: always show
  // - Single-use: show unless a code exists
  const showAddButton = !singleUse || (singleUse && !hasItems);

  const handleRemove = async (id) => {
    await onRemove?.(id);
    // Reset to starting state after removing a single-use code
    if (singleUse) {
      setOpen(false);  // collapsed start
      setErr('');
      setCode('');
    }
  };

  return (
    <div className="space-y-2">
      {/* Header line with Add button (hidden only when singleUse + code present) */}
      <div className="flex items-center justify-between gap-4">
        <div className="text-black">{title}</div>
        {showAddButton && (
          <Button
            variant="ghost"
            className="text-primary hover:text-primary w-auto"
            onClick={() => setOpen((v) => !v)}
          >
            {addButtonLabel}
          </Button>
        )}
      </div>

      {/* Codes column (each row with inline right text) */}
      {hasItems && (
        <div className="flex flex-col gap-2">
          {items.map(({ id, rightText }) => (
            <div key={id} className="flex items-center justify-between gap-4">
              <Badge
                variant="light"
                className="inline-flex items-center gap-2 px-3 py-1 text-base"
              >
                <span className="font-medium">{id}</span>
                <button
                  type="button"
                  aria-label={`Remove ${id}`}
                  className="leading-none transition-opacity hover:opacity-70"
                  onClick={() => handleRemove(id)}
                >
                  ×
                </button>
              </Badge>

              {rightText ? (
                <div className="text-primary font-semibold">{rightText}</div>
              ) : (
                <div className="text-muted-foreground">&nbsp;</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Input (toggled), with same UX as multi-use; hidden when singleUse already has a code */}
      {showInput && (
        <div className="space-y-2">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={placeholder}
              className="w-full"
            />
            <div className="flex gap-2 sm:ml-2 align-center">
                <X className="h-4 w-4 text-red-500 cursor-pointer" onClick={() => {setOpen(false)}} />
              <Button
                onClick={handleApply}
                className="w-full sm:w-auto rounded-full bg-primary/10 text-primary hover:bg-primary/20"
                variant="ghost"
                disabled={loading}
              >
              
                {loading ? checkingLabel : applyButtonLabel}
              </Button>
            </div>
          </div>
          {err && <p className="text-sm text-red-600">{err}</p>}
        </div>
      )}
    </div>
  );
}
